/**
 * Customer (Account) Detail Page - P0 Enhanced
 * 
 * Sections:
 * - Header with Back, Edit, + New Order buttons
 * - Overview (merged): Name, Salesperson, Emails, Phone, Tax, Net Terms, Default Ship-To
 * - Orders Preview: 5 most recent + New Order + View All
 * - Sidebar: Contacts (3), Tasks (3), Activities (3), Documents - all with + buttons
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { crmAPI, adminAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useRefetchOnReturn } from '../../hooks';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Building2,
  Phone,
  Globe,
  MapPin,
  FileText,
  User,
  Mail,
  ClipboardList,
  ShoppingCart,
  Activity,
  Plus,
  Loader2,
  ExternalLink,
  Truck,
  Edit2,
  Upload,
  Download,
  Trash2,
  Clock,
  ArrowLeft,
  Eye,
  File,
  DollarSign,
} from 'lucide-react';

// Import shared components from Detail Page System
import { DetailHeader, RelatedOrdersTable } from '../../components/detail-system';
import CreatePaymentModal from '../../components/admin/CreatePaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Net Terms options
const NET_TERMS_OPTIONS = [
  { value: 0, label: 'Prepay (Due immediately)' },
  { value: 5, label: 'Net 5' },
  { value: 10, label: 'Net 10' },
  { value: 15, label: 'Net 15' },
  { value: 20, label: 'Net 20' },
  { value: 25, label: 'Net 25' },
  { value: 30, label: 'Net 30' },
  { value: 35, label: 'Net 35' },
  { value: 40, label: 'Net 40' },
  { value: 45, label: 'Net 45' },
  { value: 50, label: 'Net 50' },
];

// Tax exempt reasons
const TAX_EXEMPT_REASONS = [
  { value: 'resale', label: 'Resale' },
  { value: 'nonprofit', label: 'Non-profit' },
  { value: 'other', label: 'Other' },
];

// Document types
const DOCUMENT_TYPES = [
  { value: 'resale_certificate', label: 'Resale Certificate' },
  { value: 'tax_exempt_certificate', label: 'Tax Exempt Certificate' },
  { value: 'other', label: 'Other Document' },
];

// Activity types for display
const ACTIVITY_TYPE_LABELS = {
  call: 'Call',
  email: 'Email',
  walk_in: 'Walk-in',
  meeting: 'Meeting',
  note: 'Note',
  document_upload: 'Document Upload',
  customer_update: 'Customer Update',
  contact_added: 'Contact Added',
  order_created: 'Order Created',
  task_created: 'Task Created',
};

// Status badge helper
const getStatusBadge = (status) => {
  const variants = {
    OPEN: 'bg-blue-100 text-blue-700',
    IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
    COMPLETED: 'bg-green-100 text-green-700',
    CANCELLED: 'bg-gray-100 text-gray-500',
    NONE: 'bg-gray-100 text-gray-500',
    ISSUED: 'bg-blue-100 text-blue-700',
    SENT: 'bg-purple-100 text-purple-700',
    PAID: 'bg-green-100 text-green-700',
    VOID: 'bg-red-100 text-red-700',
  };
  return variants[status] || 'bg-gray-100 text-gray-500';
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Format address
const formatAddress = (addr) => {
  if (!addr || !addr.address_line1) return null;
  const parts = [addr.address_line1];
  if (addr.address_line2) parts.push(addr.address_line2);
  parts.push(`${addr.city || ''}, ${addr.state || ''} ${addr.zip_code || ''}`.trim());
  return parts;
};

const CustomerDetailPage = ({ customerId: propCustomerId, onBack, onNavigateToContact, onNavigateToOrder, onNavigateToTask, onCreateOrder, onReorderFromOrders }) => {
  const { customerId: paramCustomerId } = useParams();
  const customerId = propCustomerId || paramCustomerId;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [salesperson, setSalesperson] = useState(null);
  const [defaultShipTo, setDefaultShipTo] = useState(null);
  const [contacts, setContacts] = useState({ items: [], total: 0 });
  const [orders, setOrders] = useState({ items: [], total: 0 });
  const [tasks, setTasks] = useState({ items: [], total: 0 });
  const [activities, setActivities] = useState({ items: [], total: 0 });
  const [documents, setDocuments] = useState({ items: [], total: 0 });
  const [internalUsers, setInternalUsers] = useState([]); // For salesperson/owner dropdowns
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [showDocPreview, setShowDocPreview] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('other');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    primary_email: '',
    billing_email: '',
    website: '',
    net_terms_days: null,
    tax_exempt: false,
    tax_exempt_reason: '',
    notes: '',
    billing_address: { address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'USA' },
    shipping_address: { address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'USA' },
    owner_user_id: '',
  });
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    title: '',
    is_primary: false,
    is_default_ship_to: false,
    shipping_address: { address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'USA' },
    delivery_notes: ''
  });

  // Task form state
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    due_at: '',
    owner_user_id: '',
    priority: 'med',
  });

  // Activity form state
  const [activityForm, setActivityForm] = useState({
    type: 'note',
    subject: '',
    body: '',
  });

  // Get user role from auth context
  const userRole = user?.role || 'customer';
  const canUploadDocs = ['admin', 'sales'].includes(userRole);
  const canDeleteDocs = userRole === 'admin';

  // Fetch customer detail
  const fetchCustomerDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await crmAPI.getAccountDetail(customerId);
      
      setCustomer(response.account);
      setSalesperson(response.salesperson);
      setDefaultShipTo(response.default_ship_to_contact);
      setContacts(response.contacts || { items: [], total: 0 });
      setOrders(response.orders || { items: [], total: 0 });
      setTasks(response.tasks || { items: [], total: 0 });
      setActivities(response.activities || { items: [], total: 0 });
      setDocuments(response.documents || { items: [], total: 0 });
    } catch (err) {
      console.error('Failed to fetch customer:', err);
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchCustomerDetail();
  }, [fetchCustomerDetail]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchCustomerDetail();
      toast.success('Data refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refetch on tab focus or route return
  useRefetchOnReturn({
    refetch: fetchCustomerDetail,
    enabled: !loading && !!customerId,
    refreshing,
    throttleMs: 1500,
  });

  // Fetch internal users for salesperson dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await adminAPI.getUsers({ page_size: 200 });
        // Filter to internal users only (exclude customers)
        const internal = (data.users || []).filter(u => 
          u.role !== 'customer' && u.role !== 'pending'
        );
        setInternalUsers(internal);
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };
    fetchUsers();
  }, []);

  // Handle edit modal open
  const handleOpenEditModal = () => {
    if (!customer) return;
    setEditForm({
      name: customer.name || '',
      phone: customer.phone || '',
      primary_email: customer.primary_email || '',
      billing_email: customer.billing_email || '',
      website: customer.website || '',
      net_terms_days: customer.net_terms_days ?? null,
      tax_exempt: customer.tax_exempt || false,
      tax_exempt_reason: customer.tax_exempt_reason || '',
      notes: customer.notes || '',
      billing_address: {
        address_line1: customer.billing_address?.address_line1 || '',
        address_line2: customer.billing_address?.address_line2 || '',
        city: customer.billing_address?.city || '',
        state: customer.billing_address?.state || '',
        zip_code: customer.billing_address?.zip_code || '',
        country: customer.billing_address?.country || 'USA'
      },
      shipping_address: {
        address_line1: customer.shipping_address?.address_line1 || '',
        address_line2: customer.shipping_address?.address_line2 || '',
        city: customer.shipping_address?.city || '',
        state: customer.shipping_address?.state || '',
        zip_code: customer.shipping_address?.zip_code || '',
        country: customer.shipping_address?.country || 'USA'
      },
      owner_user_id: customer.owner_user_id || '',
    });
    setShowEditModal(true);
  };

  // Handle update customer
  const handleUpdateCustomer = async () => {
    if (editForm.tax_exempt && !editForm.tax_exempt_reason) {
      toast.error('Tax exempt reason is required');
      return;
    }
    
    const cleanAddress = (addr) => {
      if (!addr || !addr.address_line1) return null;
      return {
        address_line1: addr.address_line1,
        address_line2: addr.address_line2 || '',
        city: addr.city || '',
        state: addr.state || '',
        zip_code: addr.zip_code || '',
        country: addr.country || 'USA'
      };
    };
    
    try {
      setSaving(true);
      await crmAPI.updateAccount(customerId, {
        name: editForm.name,
        phone: editForm.phone,
        primary_email: editForm.primary_email || null,
        billing_email: editForm.billing_email || null,
        website: editForm.website,
        net_terms_days: editForm.net_terms_days,
        tax_exempt: editForm.tax_exempt,
        tax_exempt_reason: editForm.tax_exempt ? editForm.tax_exempt_reason : null,
        notes: editForm.notes,
        billing_address: cleanAddress(editForm.billing_address),
        shipping_address: cleanAddress(editForm.shipping_address),
        owner_user_id: editForm.owner_user_id || null,
      });
      toast.success('Customer updated');
      setShowEditModal(false);
      fetchCustomerDetail();
    } catch (err) {
      toast.error('Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  // Handle create contact
  const handleCreateContact = async () => {
    if (!contactForm.first_name && !contactForm.last_name) {
      toast.error('Name is required');
      return;
    }
    
    // Clean shipping address - only include if address_line1 is filled
    // Backend expects postal_code, not zip_code
    const cleanShippingAddress = (addr) => {
      if (!addr || !addr.address_line1) return null;
      return {
        address_line1: addr.address_line1,
        address_line2: addr.address_line2 || null,
        city: addr.city || '',
        state: addr.state || '',
        postal_code: addr.zip_code || '',  // Map zip_code to postal_code
        country: addr.country || 'USA'
      };
    };
    
    try {
      setSaving(true);
      await crmAPI.createContact({
        first_name: contactForm.first_name || null,
        last_name: contactForm.last_name || null,
        email: contactForm.email || null,
        phone: contactForm.phone || null,
        title: contactForm.title || null,
        is_primary: contactForm.is_primary,
        is_default_ship_to: contactForm.is_default_ship_to,
        shipping_address: cleanShippingAddress(contactForm.shipping_address),
        delivery_notes: contactForm.delivery_notes || null,
        account_id: customerId
      });
      toast.success('Contact added');
      setShowAddContactModal(false);
      setContactForm({
        first_name: '', last_name: '', email: '', phone: '', title: '',
        is_primary: false, is_default_ship_to: false,
        shipping_address: { address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'USA' },
        delivery_notes: ''
      });
      fetchCustomerDetail();
    } catch (err) {
      console.error('Failed to add contact:', err);
      toast.error(err.message || 'Failed to add contact');
    } finally {
      setSaving(false);
    }
  };

  // Handle open edit contact modal
  const handleOpenEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.title || '',
      is_primary: contact.is_primary || false,
      is_default_ship_to: contact.is_default_ship_to || false,
      shipping_address: {
        address_line1: contact.shipping_address?.address_line1 || '',
        address_line2: contact.shipping_address?.address_line2 || '',
        city: contact.shipping_address?.city || '',
        state: contact.shipping_address?.state || '',
        zip_code: contact.shipping_address?.postal_code || contact.shipping_address?.zip_code || '',
        country: contact.shipping_address?.country || 'USA'
      },
      delivery_notes: contact.delivery_notes || ''
    });
    setShowEditContactModal(true);
  };

  // Handle update contact
  const handleUpdateContact = async () => {
    if (!contactForm.first_name && !contactForm.last_name) {
      toast.error('Name is required');
      return;
    }
    
    // Clean shipping address - only include if address_line1 is filled
    const cleanShippingAddress = (addr) => {
      if (!addr || !addr.address_line1) return null;
      return {
        address_line1: addr.address_line1,
        address_line2: addr.address_line2 || null,
        city: addr.city || '',
        state: addr.state || '',
        postal_code: addr.zip_code || '',
        country: addr.country || 'USA'
      };
    };
    
    try {
      setSaving(true);
      await crmAPI.updateContact(editingContact.id, {
        first_name: contactForm.first_name || null,
        last_name: contactForm.last_name || null,
        email: contactForm.email || null,
        phone: contactForm.phone || null,
        title: contactForm.title || null,
        is_primary: contactForm.is_primary,
        is_default_ship_to: contactForm.is_default_ship_to,
        shipping_address: cleanShippingAddress(contactForm.shipping_address),
        delivery_notes: contactForm.delivery_notes || null
      });
      toast.success('Contact updated');
      setShowEditContactModal(false);
      setEditingContact(null);
      setContactForm({
        first_name: '', last_name: '', email: '', phone: '', title: '',
        is_primary: false, is_default_ship_to: false,
        shipping_address: { address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'USA' },
        delivery_notes: ''
      });
      fetchCustomerDetail();
    } catch (err) {
      console.error('Failed to update contact:', err);
      toast.error(err.message || 'Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  // Handle create task
  const handleCreateTask = async () => {
    if (!taskForm.title) {
      toast.error('Task title is required');
      return;
    }
    if (!taskForm.due_at) {
      toast.error('Due date is required');
      return;
    }
    
    try {
      setSaving(true);
      await crmAPI.createTask({
        title: taskForm.title,
        description: taskForm.description || null,
        due_at: taskForm.due_at,
        account_id: customerId,
        owner_user_id: taskForm.owner_user_id || null,
        priority: taskForm.priority || 'med',
        status: 'open'
      });
      toast.success('Task created');
      setShowAddTaskModal(false);
      setTaskForm({ title: '', description: '', due_at: '', owner_user_id: '', priority: 'med' });
      fetchCustomerDetail();
    } catch (err) {
      console.error('Failed to create task:', err);
      toast.error(err.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  // Handle create activity
  const handleCreateActivity = async () => {
    if (!activityForm.subject) {
      toast.error('Activity subject is required');
      return;
    }
    
    try {
      setSaving(true);
      await crmAPI.createActivity({
        type: activityForm.type,
        subject: activityForm.subject,
        body: activityForm.body || null,
        account_id: customerId,
        occurred_at: new Date().toISOString()
      });
      toast.success('Activity logged');
      setShowAddActivityModal(false);
      setActivityForm({ type: 'note', subject: '', body: '' });
      fetchCustomerDetail();
    } catch (err) {
      console.error('Failed to log activity:', err);
      toast.error(err.message || 'Failed to log activity');
    } finally {
      setSaving(false);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PDF, JPG, PNG');
      return;
    }
    
    // Validate size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max: 10MB');
      return;
    }
    
    try {
      setUploadingDoc(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', selectedDocType);
      
      const response = await fetch(`${API_URL}/api/crm/customers/${customerId}/documents`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Upload failed');
      }
      
      toast.success('Document uploaded');
      setShowUploadDocModal(false);
      setSelectedDocType('other');
      fetchCustomerDetail();
    } catch (err) {
      toast.error(err.message || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle document delete
  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    
    try {
      await crmAPI.deleteCustomerDocument(docId);
      toast.success('Document deleted');
      fetchCustomerDetail();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  // Handle new order
  const handleNewOrder = () => {
    if (onCreateOrder && customer) {
      onCreateOrder({
        id: customer.id,
        name: customer.name,
        email: customer.primary_email,
        phone: customer.phone,
      });
    }
  };

  // Handle reorder from single order
  const handleReorder = (orderId) => {
    if (onReorderFromOrders) {
      onReorderFromOrders([orderId]);
    }
  };

  // Handle combine multiple orders
  const handleCombineOrders = (orderIds) => {
    if (onReorderFromOrders && orderIds.length > 0) {
      onReorderFromOrders(orderIds);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-6 text-center text-gray-500">
        Customer not found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-detail-page">
      {/* Header - Using shared DetailHeader component */}
      <DetailHeader
        onBack={() => onBack ? onBack() : navigate('/admin?tab=customers')}
        backLabel="Back"
        entityName={customer.name}
        entityId={customerId}
        entityIcon={Building2}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        primaryActions={[
          { label: 'Edit', icon: Edit2, onClick: handleOpenEditModal, testId: 'edit-customer-btn' },
        ]}
        secondaryActions={[
          { label: 'Receive Payment', icon: DollarSign, onClick: () => setShowPaymentModal(true), testId: 'receive-payment-btn' },
          { label: 'Statement', icon: FileText, onClick: () => navigate(`/admin?tab=customer-statements&account_id=${customerId}`), testId: 'view-statement-btn' },
          { label: 'New Order', icon: Plus, onClick: handleNewOrder, testId: 'new-order-header-btn' },
        ]}
        testId="customer-detail-header"
      />

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Overview Section (Merged) */}
            <div className="bg-white rounded-lg border p-6" data-testid="overview-section">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Overview
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Customer Name</label>
                    <p className="font-medium">{customer.name}</p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Salesperson</label>
                    <p className="font-medium">
                      {salesperson ? salesperson.name : <span className="text-gray-400">Not assigned</span>}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Primary Email</label>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {customer.primary_email || <span className="text-gray-400">—</span>}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Billing Email</label>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {customer.billing_email || <span className="text-gray-400">—</span>}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Phone</label>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {customer.phone || <span className="text-gray-400">—</span>}
                    </p>
                  </div>
                </div>
                
                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Tax Status</label>
                    <div>
                      {customer.tax_exempt ? (
                        <Badge className="bg-green-100 text-green-700">
                          Exempt ({customer.tax_exempt_reason})
                        </Badge>
                      ) : (
                        <Badge variant="outline">Taxable</Badge>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Net Terms</label>
                    <p className="font-medium">
                      {customer.net_terms_days === 0 ? 'Prepay' : 
                       customer.net_terms_days ? `Net ${customer.net_terms_days}` : 
                       <span className="text-gray-400">Not set</span>}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Default Ship-To Contact</label>
                    {defaultShipTo ? (
                      <div className="text-sm">
                        <p className="font-medium">
                          {defaultShipTo.first_name} {defaultShipTo.last_name}
                        </p>
                        {defaultShipTo.shipping_address && (
                          <p className="text-gray-500 text-xs">
                            {defaultShipTo.shipping_address.city}, {defaultShipTo.shipping_address.state}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">None set</p>
                    )}
                  </div>
                  
                  {customer.website && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Website</label>
                      <p className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <a href={customer.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {customer.website}
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Addresses */}
              <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t">
                <div>
                  <label className="text-xs text-gray-500 uppercase flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Billing Address
                  </label>
                  {formatAddress(customer.billing_address) ? (
                    <div className="text-sm mt-1">
                      {formatAddress(customer.billing_address).map((line, i) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">Not set</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase flex items-center gap-1">
                    <Truck className="w-3 h-3" /> Shipping Address
                  </label>
                  {formatAddress(customer.shipping_address) ? (
                    <div className="text-sm mt-1">
                      {formatAddress(customer.shipping_address).map((line, i) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">Not set</p>
                  )}
                </div>
              </div>
              
              {/* Timestamps */}
              <div className="flex items-center gap-6 mt-6 pt-4 border-t text-xs text-gray-500">
                {customer.created_at && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>Created: {formatDate(customer.created_at)}</span>
                  </div>
                )}
                {customer.updated_at && customer.updated_at !== customer.created_at && (
                  <div className="flex items-center gap-1" data-testid="customer-updated-at">
                    <Clock className="w-3 h-3" />
                    <span>Updated: {formatDate(customer.updated_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Orders Preview - Using shared RelatedOrdersTable component */}
            <RelatedOrdersTable
              orders={orders.items}
              total={orders.total}
              onViewOrder={(orderId) => onNavigateToOrder ? onNavigateToOrder(orderId) : navigate(`/admin/orders/${orderId}`)}
              onViewAll={() => navigate(`/admin?tab=orders&account_id=${customerId}&account_name=${encodeURIComponent(customer.name)}`)}
              onCreateOrder={handleNewOrder}
              onReorder={onReorderFromOrders ? handleReorder : null}
              onCombineOrders={onReorderFromOrders ? handleCombineOrders : null}
              showCreateButton={true}
              showReorderActions={!!onReorderFromOrders}
              maxItems={5}
              testIdPrefix="customer-orders"
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* Contacts Preview */}
            <div className="bg-white rounded-lg border p-4" data-testid="contacts-sidebar">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  Contacts
                  {contacts.total > 0 && <Badge variant="outline" className="text-xs">{contacts.total}</Badge>}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAddContactModal(true)}
                  data-testid="add-contact-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {contacts.items.length > 0 ? (
                <div className="space-y-2">
                  {contacts.items.map(contact => (
                    <div key={contact.id} className="p-2 rounded hover:bg-gray-50 text-sm group">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                          <p className="text-gray-500 text-xs truncate">{contact.email || contact.phone || '—'}</p>
                          {contact.is_default_ship_to && (
                            <Badge className="text-xs bg-blue-50 text-blue-600 mt-1">Default Ship-To</Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditContact(contact);
                          }}
                          data-testid={`edit-contact-${contact.id}`}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm text-center py-4">No contacts</p>
              )}
              
              {contacts.total > 3 && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="w-full mt-2"
                  onClick={() => navigate(`/admin?tab=crm-contacts&account_id=${customerId}&account_name=${encodeURIComponent(customer.name)}`)}
                  data-testid="view-all-contacts-btn"
                >
                  View All ({contacts.total})
                </Button>
              )}
            </div>

            {/* Tasks Preview */}
            <div className="bg-white rounded-lg border p-4" data-testid="tasks-sidebar">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-amber-600" />
                  Tasks
                  {tasks.total > 0 && <Badge variant="outline" className="text-xs">{tasks.total}</Badge>}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAddTaskModal(true)}
                  data-testid="add-task-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {tasks.items.length > 0 ? (
                <div className="space-y-2">
                  {tasks.items.map(task => (
                    <div key={task.id} className="p-2 rounded hover:bg-gray-50 text-sm">
                      <p className="font-medium">{task.title}</p>
                      <p className="text-gray-500 text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Due: {formatDate(task.due_at)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm text-center py-4">No tasks</p>
              )}
              
              {tasks.total > 3 && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="w-full mt-2"
                  onClick={() => navigate(`/admin?tab=tasks&account_id=${customerId}&account_name=${encodeURIComponent(customer.name)}`)}
                  data-testid="view-all-tasks-btn"
                >
                  View All ({tasks.total})
                </Button>
              )}
            </div>

            {/* Activities Preview */}
            <div className="bg-white rounded-lg border p-4" data-testid="activities-sidebar">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-600" />
                  Activities
                  {activities.total > 0 && <Badge variant="outline" className="text-xs">{activities.total}</Badge>}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAddActivityModal(true)}
                  data-testid="add-activity-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {activities.items.length > 0 ? (
                <div className="space-y-2">
                  {activities.items.map(activity => (
                    <div key={activity.id} className="p-2 rounded hover:bg-gray-50 text-sm">
                      <p className="font-medium">{ACTIVITY_TYPE_LABELS[activity.type] || activity.type}</p>
                      <p className="text-gray-600 text-xs truncate">{activity.subject}</p>
                      <p className="text-gray-500 text-xs">{formatDate(activity.occurred_at)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm text-center py-4">No activities</p>
              )}
              
              {activities.total > 3 && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="w-full mt-2"
                  onClick={() => navigate(`/admin?tab=activities&account_id=${customerId}&account_name=${encodeURIComponent(customer.name)}`)}
                  data-testid="view-all-activities-btn"
                >
                  View All ({activities.total})
                </Button>
              )}
            </div>

            {/* Customer Documents (in sidebar) */}
            <div className="bg-white rounded-lg border p-4" data-testid="documents-sidebar">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-600" />
                  Documents
                  {documents.total > 0 && <Badge variant="outline" className="text-xs">{documents.total}</Badge>}
                </h3>
                {canUploadDocs && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowUploadDocModal(true)}
                    data-testid="add-document-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              {documents.items.length > 0 ? (
                <div className="space-y-2">
                  {documents.items.slice(0, 3).map(doc => (
                    <div key={doc.id} className="p-2 rounded hover:bg-gray-50 text-sm flex items-center justify-between" data-testid={`doc-${doc.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-xs truncate">{doc.file_name}</p>
                          <p className="text-gray-500 text-xs">
                            {DOCUMENT_TYPES.find(d => d.value === doc.document_type)?.label || 'Other'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => setShowDocPreview(doc)}
                          data-testid={`preview-doc-${doc.id}`}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => window.open(`${API_URL}${doc.storage_url}`, '_blank')}
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                        {canDeleteDocs && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteDocument(doc.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-400">
                  <FileText className="w-8 h-8 mx-auto mb-1 opacity-50" />
                  <p className="text-xs">No documents</p>
                </div>
              )}
              
              {documents.total > 3 && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="w-full mt-2"
                  data-testid="view-all-documents-btn"
                >
                  View All ({documents.total})
                </Button>
              )}
            </div>

            {/* Notes */}
            {customer.notes && (
              <div className="bg-white rounded-lg border p-4">
                <h3 className="font-semibold mb-2">Notes</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{customer.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Customer Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-customer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Edit Customer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="customer-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Primary Email</Label>
                <Input
                  type="email"
                  value={editForm.primary_email}
                  onChange={(e) => setEditForm({ ...editForm, primary_email: e.target.value })}
                  placeholder="contact@company.com"
                  data-testid="primary-email-input"
                />
              </div>
              <div>
                <Label>Billing Email</Label>
                <Input
                  type="email"
                  value={editForm.billing_email}
                  onChange={(e) => setEditForm({ ...editForm, billing_email: e.target.value })}
                  placeholder="billing@company.com"
                  data-testid="billing-email-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={editForm.website}
                  onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Net Terms</Label>
                <Select 
                  value={editForm.net_terms_days?.toString() || 'none'} 
                  onValueChange={(v) => setEditForm({ ...editForm, net_terms_days: v === 'none' ? null : parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {NET_TERMS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Salesperson</Label>
                <Select
                  value={editForm.owner_user_id || 'none'}
                  onValueChange={(v) => setEditForm({ ...editForm, owner_user_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="salesperson-select">
                    <SelectValue placeholder="Select salesperson" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {internalUsers.map(user => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.name || user.email} ({user.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Tax Exempt */}
            <div className="p-3 bg-gray-50 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="tax_exempt"
                  checked={editForm.tax_exempt}
                  onChange={(e) => setEditForm({ ...editForm, tax_exempt: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="tax_exempt" className="cursor-pointer">Tax Exempt</Label>
              </div>
              {editForm.tax_exempt && (
                <Select 
                  value={editForm.tax_exempt_reason} 
                  onValueChange={(v) => setEditForm({ ...editForm, tax_exempt_reason: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {TAX_EXEMPT_REASONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
              />
            </div>
            
            {/* Billing Address */}
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Billing Address
              </h4>
              <Input
                value={editForm.billing_address?.address_line1 || ''}
                onChange={(e) => setEditForm({ ...editForm, billing_address: { ...editForm.billing_address, address_line1: e.target.value }})}
                placeholder="Address Line 1"
              />
              <Input
                value={editForm.billing_address?.address_line2 || ''}
                onChange={(e) => setEditForm({ ...editForm, billing_address: { ...editForm.billing_address, address_line2: e.target.value }})}
                placeholder="Address Line 2"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={editForm.billing_address?.city || ''}
                  onChange={(e) => setEditForm({ ...editForm, billing_address: { ...editForm.billing_address, city: e.target.value }})}
                  placeholder="City"
                />
                <Input
                  value={editForm.billing_address?.state || ''}
                  onChange={(e) => setEditForm({ ...editForm, billing_address: { ...editForm.billing_address, state: e.target.value }})}
                  placeholder="State"
                />
                <Input
                  value={editForm.billing_address?.zip_code || ''}
                  onChange={(e) => setEditForm({ ...editForm, billing_address: { ...editForm.billing_address, zip_code: e.target.value }})}
                  placeholder="ZIP"
                />
              </div>
            </div>
            
            {/* Shipping Address */}
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium flex items-center gap-2">
                <Truck className="w-4 h-4" /> Shipping Address
              </h4>
              <Input
                value={editForm.shipping_address?.address_line1 || ''}
                onChange={(e) => setEditForm({ ...editForm, shipping_address: { ...editForm.shipping_address, address_line1: e.target.value }})}
                placeholder="Address Line 1"
              />
              <Input
                value={editForm.shipping_address?.address_line2 || ''}
                onChange={(e) => setEditForm({ ...editForm, shipping_address: { ...editForm.shipping_address, address_line2: e.target.value }})}
                placeholder="Address Line 2"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={editForm.shipping_address?.city || ''}
                  onChange={(e) => setEditForm({ ...editForm, shipping_address: { ...editForm.shipping_address, city: e.target.value }})}
                  placeholder="City"
                />
                <Input
                  value={editForm.shipping_address?.state || ''}
                  onChange={(e) => setEditForm({ ...editForm, shipping_address: { ...editForm.shipping_address, state: e.target.value }})}
                  placeholder="State"
                />
                <Input
                  value={editForm.shipping_address?.zip_code || ''}
                  onChange={(e) => setEditForm({ ...editForm, shipping_address: { ...editForm.shipping_address, zip_code: e.target.value }})}
                  placeholder="ZIP"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateCustomer} disabled={saving || !editForm.name}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contact Modal */}
      <Dialog open={showAddContactModal} onOpenChange={setShowAddContactModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-contact-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Add Contact
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={contactForm.first_name}
                  onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                  data-testid="contact-first-name"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={contactForm.last_name}
                  onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                  data-testid="contact-last-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Title</Label>
              <Input
                value={contactForm.title}
                onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                placeholder="e.g., Purchasing Manager"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_primary"
                  checked={contactForm.is_primary}
                  onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_primary" className="cursor-pointer text-sm">Primary Contact</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default_ship_to"
                  checked={contactForm.is_default_ship_to}
                  onChange={(e) => setContactForm({ ...contactForm, is_default_ship_to: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_default_ship_to" className="cursor-pointer text-sm">Default Ship-To</Label>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium flex items-center gap-2 text-sm">
                <Truck className="w-4 h-4" /> Shipping Address
              </h4>
              <Input
                value={contactForm.shipping_address?.address_line1 || ''}
                onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, address_line1: e.target.value }})}
                placeholder="Address Line 1"
              />
              <Input
                value={contactForm.shipping_address?.address_line2 || ''}
                onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, address_line2: e.target.value }})}
                placeholder="Address Line 2"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={contactForm.shipping_address?.city || ''}
                  onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, city: e.target.value }})}
                  placeholder="City"
                />
                <Input
                  value={contactForm.shipping_address?.state || ''}
                  onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, state: e.target.value }})}
                  placeholder="State"
                />
                <Input
                  value={contactForm.shipping_address?.zip_code || ''}
                  onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, zip_code: e.target.value }})}
                  placeholder="ZIP"
                />
              </div>
            </div>
            
            <div>
              <Label>Delivery Notes</Label>
              <Textarea
                value={contactForm.delivery_notes}
                onChange={(e) => setContactForm({ ...contactForm, delivery_notes: e.target.value })}
                placeholder="Special delivery instructions..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContactModal(false)}>Cancel</Button>
            <Button onClick={handleCreateContact} disabled={saving || (!contactForm.first_name && !contactForm.last_name)}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Modal */}
      <Dialog open={showEditContactModal} onOpenChange={(open) => {
        setShowEditContactModal(open);
        if (!open) {
          setEditingContact(null);
          setContactForm({
            first_name: '', last_name: '', email: '', phone: '', title: '',
            is_primary: false, is_default_ship_to: false,
            shipping_address: { address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'USA' },
            delivery_notes: ''
          });
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-contact-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Edit Contact
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={contactForm.first_name}
                  onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                  data-testid="edit-contact-first-name"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={contactForm.last_name}
                  onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                  data-testid="edit-contact-last-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Title</Label>
              <Input
                value={contactForm.title}
                onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                placeholder="e.g., Purchasing Manager"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_is_primary"
                  checked={contactForm.is_primary}
                  onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="edit_is_primary" className="cursor-pointer text-sm">Primary Contact</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_is_default_ship_to"
                  checked={contactForm.is_default_ship_to}
                  onChange={(e) => setContactForm({ ...contactForm, is_default_ship_to: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="edit_is_default_ship_to" className="cursor-pointer text-sm">Default Ship-To</Label>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium flex items-center gap-2 text-sm">
                <Truck className="w-4 h-4" /> Shipping Address
              </h4>
              <Input
                value={contactForm.shipping_address?.address_line1 || ''}
                onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, address_line1: e.target.value }})}
                placeholder="Address Line 1"
              />
              <Input
                value={contactForm.shipping_address?.address_line2 || ''}
                onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, address_line2: e.target.value }})}
                placeholder="Address Line 2"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={contactForm.shipping_address?.city || ''}
                  onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, city: e.target.value }})}
                  placeholder="City"
                />
                <Input
                  value={contactForm.shipping_address?.state || ''}
                  onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, state: e.target.value }})}
                  placeholder="State"
                />
                <Input
                  value={contactForm.shipping_address?.zip_code || ''}
                  onChange={(e) => setContactForm({ ...contactForm, shipping_address: { ...contactForm.shipping_address, zip_code: e.target.value }})}
                  placeholder="ZIP"
                />
              </div>
            </div>
            
            <div>
              <Label>Delivery Notes</Label>
              <Textarea
                value={contactForm.delivery_notes}
                onChange={(e) => setContactForm({ ...contactForm, delivery_notes: e.target.value })}
                placeholder="Special delivery instructions..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditContactModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateContact} disabled={saving || (!contactForm.first_name && !contactForm.last_name)}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Modal */}
      <Dialog open={showAddTaskModal} onOpenChange={setShowAddTaskModal}>
        <DialogContent className="max-w-md" data-testid="add-task-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Add Task
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Task Title *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                placeholder="e.g., Follow up on quote"
                data-testid="task-title"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date *</Label>
                <Input
                  type="datetime-local"
                  value={taskForm.due_at}
                  onChange={(e) => setTaskForm({ ...taskForm, due_at: e.target.value })}
                  data-testid="task-due-date"
                />
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}
                >
                  <SelectTrigger data-testid="task-priority">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="med">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Assign To</Label>
              <Select
                value={taskForm.owner_user_id || 'unassigned'}
                onValueChange={(v) => setTaskForm({ ...taskForm, owner_user_id: v === 'unassigned' ? '' : v })}
              >
                <SelectTrigger data-testid="task-owner">
                  <SelectValue placeholder="Select assignee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {internalUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name || user.email} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Task details..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTaskModal(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={saving || !taskForm.title || !taskForm.due_at}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Activity Modal */}
      <Dialog open={showAddActivityModal} onOpenChange={setShowAddActivityModal}>
        <DialogContent className="max-w-md" data-testid="add-activity-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Log Activity
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Activity Type</Label>
              <Select 
                value={activityForm.type} 
                onValueChange={(v) => setActivityForm({ ...activityForm, type: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="walk_in">Walk-in</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Subject *</Label>
              <Input
                value={activityForm.subject}
                onChange={(e) => setActivityForm({ ...activityForm, subject: e.target.value })}
                placeholder="e.g., Discussed pricing"
                data-testid="activity-subject"
              />
            </div>
            
            <div>
              <Label>Details</Label>
              <Textarea
                value={activityForm.body}
                onChange={(e) => setActivityForm({ ...activityForm, body: e.target.value })}
                placeholder="Activity details..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddActivityModal(false)}>Cancel</Button>
            <Button onClick={handleCreateActivity} disabled={saving || !activityForm.subject}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Log Activity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Modal */}
      <Dialog open={showUploadDocModal} onOpenChange={setShowUploadDocModal}>
        <DialogContent className="max-w-md" data-testid="upload-doc-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Document
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Document Type</Label>
              <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map(dt => (
                    <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>File (PDF, JPG, PNG - max 10MB)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                onChange={handleDocumentUpload}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDocModal(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={!!showDocPreview} onOpenChange={() => setShowDocPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="doc-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {showDocPreview?.file_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {showDocPreview && (
              <div className="flex flex-col items-center">
                {showDocPreview.file_type?.includes('pdf') ? (
                  <iframe
                    src={`${API_URL}${showDocPreview.storage_url}`}
                    className="w-full h-[60vh] border rounded"
                    title={showDocPreview.file_name}
                  />
                ) : (
                  <img
                    src={`${API_URL}${showDocPreview.storage_url}`}
                    alt={showDocPreview.file_name}
                    className="max-w-full max-h-[60vh] object-contain rounded"
                  />
                )}
                <div className="mt-4 text-sm text-gray-500">
                  <p>Type: {DOCUMENT_TYPES.find(d => d.value === showDocPreview.document_type)?.label || 'Other'}</p>
                  <p>Uploaded: {formatDate(showDocPreview.uploaded_at)}</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => window.open(`${API_URL}${showDocPreview?.storage_url}`, '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button onClick={() => setShowDocPreview(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <CreatePaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        prefillCustomer={customer ? { id: customer.id, name: customer.name } : null}
        defaultType="sales"
        onSuccess={() => {
          setShowPaymentModal(false);
          toast.success('Payment recorded successfully');
          // Refresh data to show updated balances
          fetchCustomerDetail();
        }}
      />
    </div>
  );
};

export default CustomerDetailPage;
